import { useEffect, useState } from 'react'
import { SelectPicker } from 'rsuite'
import { t } from 'i18next'
import ModalDialog from '@client/components/ModalDialog'
import Loading from '@client/components/Loading'
import ConverterListModel from '@/client/models/converter'
import project from '@client/stores/projectStore'
import { TValueMapEntry } from '@shared/valueMap'

//
// Auswahlliste B: which option of the list each old value becomes.
//
// Left the values the source column holds in the data lists, with how often;
// right the option it becomes. A value that is already an option needs nothing.
// One that is neither mapped nor an option is marked — the conversion would
// turn it into ERROR.
//
// Nothing is saved here. The answer goes into the row being edited and is
// stored with it, so Cancel on the row undoes this too.
//

type TProps = {
    converterId: string
    /** the source column key (c0..cn) */
    source: string
    /** the options of the target column, trimmed */
    labels: string[]
    value: TValueMapEntry[]
    onApply: (entries: TValueMapEntry[]) => void
    onCancel: () => void
}

type TSourceValue = { value: string; count: number }

const converterListModel = new ConverterListModel()

const TableStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gap: '0 1.5em',
    alignItems: 'center',
    marginTop: '1em',
}
const HeadStyle = {
    fontWeight: 600,
    paddingBottom: '0.4em',
    borderBottom: '1px solid var(--rs-border-primary)',
}
const CellStyle = {
    padding: '0.4em 0',
    borderBottom: '1px solid var(--rs-border-secondary)',
}
const CountStyle = { ...CellStyle, opacity: 0.6, textAlign: 'right' as const }

export default function ValueMapDialog({ converterId, source, labels, value, onApply, onCancel }: TProps) {
    const [values, setValues] = useState<TSourceValue[] | null>(null)
    const [truncated, setTruncated] = useState(false)
    const [mapping, setMapping] = useState<Record<string, string>>(() =>
        Object.fromEntries(value.map((entry) => [entry.from, entry.to]))
    )

    useEffect(() => {
        let gone = false
        const projectId = project.currentProject.value?._id || ''
        converterListModel.sourceValues(projectId, converterId, source).then((res: any) => {
            if (gone) return
            setValues(Array.isArray(res?.data?.values) ? res.data.values : [])
            setTruncated(!!res?.data?.truncated)
        })
        return () => {
            gone = true
        }
    }, [converterId, source])

    const options = labels.map((label) => ({ value: label, label: label || t('(empty)') }))
    const shown = (text: string) => (text === '' ? t('(empty)') : text)

    const buttons = [
        { label: t('Apply'), status: 'apply' },
        { label: t('Cancel'), status: 'cancel' },
    ]

    const handleDialogButtons = (status: string) => {
        if (status === 'apply') {
            onApply(
                Object.entries(mapping)
                    .filter(([, to]) => to != null)
                    .map(([from, to]) => ({ from, to }))
            )
        } else onCancel()
        return true
    }

    const pick = (from: string, to: string | null) => {
        setMapping((current) => {
            const next = { ...current }
            if (to == null) delete next[from]
            else next[from] = to
            return next
        })
    }

    return (
        <ModalDialog title={t('Map values')} buttons={buttons} onClick={handleDialogButtons} size="sm">
            <p>{t('Values of the data lists that are not an option of this list become ERROR. Choose the option each one stands for.')}</p>
            {values === null ? (
                <Loading />
            ) : values.length === 0 ? (
                <p>{t('The data lists hold no values for this column.')}</p>
            ) : (
                <div style={TableStyle} className="value-map-table">
                    <div style={HeadStyle}>{t('Value in the data list')}</div>
                    <div style={HeadStyle}>{t('Rows')}</div>
                    <div style={HeadStyle}>{t('Option')}</div>

                    {values.map((item) => {
                        const isOption = labels.includes(item.value)
                        const target = mapping[item.value]
                        const open = !isOption && target == null
                        return (
                            <div key={item.value} style={{ display: 'contents' }} className="value-map-row">
                                <div style={CellStyle} className={open ? 'value-map-open' : ''} title={open ? t('Becomes ERROR') : ''}>
                                    {shown(item.value)}
                                </div>
                                <div style={CountStyle}>{item.count}</div>
                                <div style={CellStyle}>
                                    <SelectPicker
                                        data={options}
                                        value={target ?? null}
                                        onChange={(next) => pick(item.value, next == null ? null : String(next))}
                                        placeholder={isOption ? t('Already an option') : t('Not mapped')}
                                        searchable={options.length > 10}
                                        block
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
            {truncated && <p>{t('Only the most frequent values are shown.')}</p>}
        </ModalDialog>
    )
}
